import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../api';

const avatarColors = ['#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6','#8b5cf6','#ec4899'];
const getAvatarColor = (name = '') => avatarColors[name.charCodeAt(0) % avatarColors.length];
const getInitials = (name = '') => name.slice(0, 2).toUpperCase();

const Hostreq = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const reqs = await apiGet('/api/account/host-requests');
      setRequests((reqs || []).filter(req => req.status === 'pending'));
      setError(null);
    } catch { setError('Failed to load host requests.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // The backend promotes the user (role/hostType/agencyId/hostSince), drops the
  // request and writes the notification in one call.
  const handleApprove = async (request) => {
    setProcessingId(request.userId);
    try {
      const res = await apiPost('/api/account/host-requests/approve', {
        userId: request.userId, userCustomId: request.userCustomId, hostType: request.hostType,
      });
      if (res.success === false) throw new Error(res.message);
      setRequests(prev => prev.filter(r => r.userId !== request.userId));
    } catch (e) { alert('Failed to approve request. ' + e.message); }
    finally { setProcessingId(null); }
  };

  const handleDisapprove = async (request) => {
    setProcessingId(request.userId);
    try {
      const res = await apiPost('/api/account/host-requests/disapprove', {
        userId: request.userId, userCustomId: request.userCustomId,
      });
      if (res.success === false) throw new Error(res.message);
      setRequests(prev => prev.filter(r => r.userId !== request.userId));
    } catch (e) { alert('Failed to disapprove the request. ' + e.message); }
    finally { setProcessingId(null); }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-10 h-10 rounded-full border-4 border-white/10 border-l-[#ff4d6d] animate-spin" />
      <p className="text-sm text-white/40">Loading Requests...</p>
    </div>
  );

  if (error) return (
    <div className="p-4">
      <div className="rounded-xl p-4 text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>
        {error}
      </div>
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-white">Host Requests</h1>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Review and manage incoming host applications</p>
        </div>
        {requests.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: 'rgba(251,191,36,0.12)', color: '#fde047', border: '1px solid rgba(251,191,36,0.25)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse inline-block" />
            {requests.length} pending
          </div>
        )}
      </div>

      {requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 rounded-2xl" style={{ border: '2px dashed rgba(255,255,255,0.08)' }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.08)' }}>
            <i className="fas fa-check-double text-2xl" style={{ color: 'rgba(74,222,128,0.5)' }} />
          </div>
          <p className="font-semibold text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No pending host requests</p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>All requests have been processed</p>
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
          {requests.map(request => {
            const isProcessing = processingId === request.userId;
            const isVideo = request.hostType === 'video';
            return (
              <div
                key={request.userId}
                className="rounded-2xl p-5 flex flex-col gap-4"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {/* User info */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0" style={{ background: getAvatarColor(request.username), fontSize: 16 }}>
                    {getInitials(request.username)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-white text-sm truncate">{request.username}</p>
                    <p className="font-mono text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{request.userCustomId}</p>
                  </div>
                  {/* Status badge */}
                  <span className="text-xs font-bold rounded-full px-2.5 py-1 flex-shrink-0" style={{ background: 'rgba(251,191,36,0.12)', color: '#fde047', border: '1px solid rgba(251,191,36,0.25)' }}>
                    Pending
                  </span>
                </div>

                {/* Host type */}
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: isVideo ? 'rgba(139,92,246,0.15)' : 'rgba(56,189,248,0.15)' }}>
                    <i className={`fas ${isVideo ? 'fa-video' : 'fa-microphone'} text-sm`} style={{ color: isVideo ? '#c4b5fd' : '#7dd3fc' }} />
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Requested Host Type</p>
                    <p className="font-bold text-sm text-white capitalize">{request.hostType || 'Not specified'}</p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleApprove(request)}
                    disabled={isProcessing}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all"
                    style={{ background: isProcessing ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}
                  >
                    {isProcessing ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                    Approve
                  </button>
                  <button
                    onClick={() => handleDisapprove(request)}
                    disabled={isProcessing}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all"
                    style={{ background: isProcessing ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}
                  >
                    {isProcessing ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-times" />}
                    Decline
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Hostreq;
